import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDefaultGraphTimeForForecastDay } from '../../helpers/day/graphTime';

const getInitialGraphTime = (
  selectedDate: string,
  timeZone: string | undefined,
  isToday: boolean,
) => {
  if (!timeZone) {
    return '';
  }

  return getDefaultGraphTimeForForecastDay(selectedDate, timeZone, isToday);
};

export const useWeatherForecastDayGraphTime = (
  selectedDate: string,
  timeZone: string | undefined,
  isToday: boolean,
  isActive = true,
) => {
  const [selectedGraphTime, setSelectedGraphTime] = useState(() =>
    getInitialGraphTime(selectedDate, timeZone, isToday),
  );

  useEffect(() => {
    if (!timeZone || !isActive) {
      return;
    }

    setSelectedGraphTime(
      getDefaultGraphTimeForForecastDay(selectedDate, timeZone, isToday),
    );
  }, [isActive, isToday, selectedDate, timeZone]);

  const selectedChartTime = useMemo(() => {
    if (!selectedGraphTime || !timeZone) {
      return null;
    }

    const parsed = DateTime.fromISO(selectedGraphTime, { zone: timeZone });
    return parsed.isValid ? parsed.toJSDate() : null;
  }, [selectedGraphTime, timeZone]);

  const defaultChartTime = useMemo(() => {
    if (!timeZone) {
      return undefined;
    }

    const defaultIso = getDefaultGraphTimeForForecastDay(
      selectedDate,
      timeZone,
      isToday,
    );
    const parsed = DateTime.fromISO(defaultIso, { zone: timeZone });
    return parsed.isValid ? parsed.toJSDate() : undefined;
  }, [isToday, selectedDate, timeZone]);

  const handleSelectedChartTimeChange = useCallback(
    (time: Date) => {
      if (!timeZone) {
        return;
      }

      const iso = DateTime.fromJSDate(time, { zone: timeZone }).toISO();
      if (iso) {
        setSelectedGraphTime(iso);
      }
    },
    [timeZone],
  );

  return {
    selectedGraphTime,
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
  };
};
